import { TemplateType } from '../../../constants/src';
import { BfiApi, IApiResponse } from './bfiApi';
import { SimpleCache } from '../../../components/src';
import { UserAgreementType } from '../../../constants/src';

const tokensCache = new SimpleCache(3600);

export interface ITemplateName {
    templateId: number;
    name: string;
}

export interface IToken {
    category: number;
    code: string;
    description: string;
}

export interface ITemplateVersion {
    templateId: number;
    templateName: string;
    content: string;
    initialLoanRate?: number | null;
    additionalLoanRate?: number | null;
    daysToReferralDeadline?: number | null;
    daysToContractExpiration?: number | null;
}

export interface INotes {
    templateId: number;
    notes: string;
}

export interface IPostSignIcca {
    brokerId: number;
    entityName: string;
    principalFirstName: string;
    principalMiddleName?: string | null;
    principalLastName: string;
    principalSuffixName?: string | null;
    logoUrl: string;
}

export interface IPostCountersignIcca {
    brokerAgreementId: number;
    logoUrl: string;
}

export interface IPostCountersignIcca {
    brokerAgreementId: number;
    logoUrl: string;
}

export interface ITokenResponse extends Omit<IApiResponse, 'result'> {
    result: [IToken];
}

export interface IHtmlResponse extends Omit<IApiResponse, 'result'> {
    result: string;
}

export class TemplatesApi extends BfiApi {
    _getAndCache(key: string, url: string) {
        const data = tokensCache.get(key);
        if (!data) {
            const retVal = new Promise<[IToken]>((resolve, reject) => {
                super.get<ITokenResponse>(url).then((response) => {
                    resolve(response.result);
                });
            });
            tokensCache.set(key, retVal);
            return retVal;
        }
        return data;
    }

    getTemplatesGrid(templateTypeId: number): Promise<IApiResponse> {
        return super.get(`/templates/grid?templateTypeId=${templateTypeId ?? TemplateType.ICCA}`);
    }
    getTemplateVersions(templateId: number): Promise<IApiResponse> {
        return super.get(`/templates/${templateId}/versions`);
    }
    deleteTemplate(templateId: number): Promise<IApiResponse> {
        return super.delete(`/templates/${templateId}`);
    }
    getTokens(templateTypeId: number): Promise<IApiResponse> {
        return this._getAndCache('tokens_' + templateTypeId.toString(), `/templates/tokens?templateTypeId=${templateTypeId}`);
    }
    saveTemplateVersion(model: ITemplateVersion): Promise<IApiResponse> {
        return super.post(`/templates/versions`, model);
    }
    saveTemplateName(model: ITemplateName): Promise<IApiResponse> {
        return super.post(`/templates`, model);
    }
    saveNotes(notes: INotes): Promise<IApiResponse> {
        return super.post('/templates/notes/', notes);
    }
    previewTemplate(templateId: number): Promise<IApiResponse> {
        return super.get(`/templates/${templateId}/preview`);
    }
    previewTemplateHtml(templateId: number, html: string): Promise<IApiResponse> {
        const body = {
            templateId,
            html,
        };
        return super.post(`/templates/preview`, body);
    }
    generatePdf(versionId: number): Promise<IApiResponse> {
        return super.post(`/templates/versions/${versionId}/pdf/generate`);
    }
    signIccaHtml(broker: IPostSignIcca): Promise<IHtmlResponse> {
        return super.post('/templates/icca/sign/', broker);
    }
    countersignIccaHtml(postData: IPostCountersignIcca): Promise<IHtmlResponse> {
        return super.post('/templates/icca/countersign/', postData);
    }
    getUserAgreementHtml(typeId: UserAgreementType): Promise<IHtmlResponse> {
        return super.get(`/templates/useragreements/${typeId}/html`);
    }
}
